import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { en } from '../translations/en';
import { fr } from '../translations/fr';
import { findRouteFromPath, localizedPath } from '../utils/routes';

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState('fr');
  const [translations, setTranslations] = useState(fr);

  // Sync <html lang=""> attribute with current language
  useEffect(() => {
    document.documentElement.lang = language === 'fr' ? 'fr-CA' : 'en-CA';
  }, [language]);

  const setLang = useCallback((lang) => {
    if (lang !== 'fr' && lang !== 'en') return;
    setLanguage(lang);
    setTranslations(lang === 'en' ? en : fr);
    localStorage.setItem('cjpa_language', lang);
  }, []);

  const t = translations; // Shorthand for components

  return (
    <LanguageContext.Provider value={{ language, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

// Hook for components inside /:lang/* routes — syncs URL lang and provides toggleLanguage
export function useLangSync() {
  const { lang } = useParams();
  const { language, setLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Sync language from URL param
  useEffect(() => {
    if (lang && (lang === 'fr' || lang === 'en') && lang !== language) {
      setLang(lang);
    }
  }, [lang, language, setLang]);

  const toggleLanguage = useCallback(() => {
    const newLang = language === 'en' ? 'fr' : 'en';
    const routeName = findRouteFromPath(location.pathname);
    if (routeName) {
      const newPath = localizedPath(routeName, newLang);
      // Preserve query string and hash
      navigate(newPath + location.search + location.hash);
    } else {
      // Fallback: swap /{oldLang}/... to /{newLang}/...
      const newPath = location.pathname.replace(/^\/(fr|en)/, `/${newLang}`);
      navigate(newPath + location.search + location.hash);
    }
  }, [language, location, navigate]);

  return { toggleLanguage };
}

export const useLanguage = () => useContext(LanguageContext);
